import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'tb-upload-errors',
    category: 'troubleshooting',
    title: 'Pourquoi mon téléversement a-t-il échoué ? — explication des messages d\'erreur',
    summary: 'Chaque erreur en rouge et avertissement en jaune, avec la cause et la solution.',
    body: (
      <>
        <p>
          Lorsqu'un téléversement est rejeté, une bannière <strong>rouge</strong> s'affiche avec
          une liste de problèmes. Les messages <strong>jaunes</strong> sont de simples
          avertissements — le fichier est quand même accepté. Retrouvez votre message ci-dessous.
        </p>

        <h5 className="mt-3">Erreurs rouges (fichier rejeté)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Message</th><th>Signification &amp; solution</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><Code>Missing required column: "..."</Code></td>
              <td>La ligne d'en-tête ne contient pas une colonne requise par le fichier — généralement
                une différence d'orthographe ou de casse. Les en-têtes doivent correspondre exactement,
                par exemple <Code>consumption_kwh</Code>, tout en minuscules. Corrigez la première
                ligne et téléversez à nouveau.</td>
            </tr>
            <tr>
              <td><Code>Value must be a number, got: "..."</Code></td>
              <td>Une cellule devant contenir un nombre contient autre chose — presque toujours une
                <strong> virgule décimale</strong> (<Code>3,5</Code>) ; le séparateur doit être un
                point (<Code>3.5</Code>). Pour convertir un fichier entier en une seule fois,
                consultez <ArticleLink to="data-fix-commas">Corriger un fichier utilisant des
                virgules comme séparateur décimal</ArticleLink>. Supprimez également les unités,
                espaces ou tout autre texte des cellules numériques.</td>
            </tr>
            <tr>
              <td><Code>Value must be non-negative, got: ...</Code></td>
              <td>Une valeur de consommation, de production ou de prix est négative. Ces valeurs ne
                peuvent pas être inférieures à zéro — un nombre négatif signifie généralement que
                deux colonnes ont été inversées.</td>
            </tr>
            <tr>
              <td><Code>Invalid timestamp format: "..."</Code></td>
              <td>Une cellule date/heure n'a pas pu être lue. Utilisez le format
                <Code>2025-01-01T14:00:00Z</Code>. Consultez <ArticleLink to="data-csv-basics">Créer
                des fichiers CSV correctement</ArticleLink> pour les formats de date acceptés et
                une formule permettant de corriger une colonne entière en une fois.</td>
            </tr>
            <tr>
              <td><Code>CSV file contains no data rows</Code></td>
              <td>Le fichier ne contient qu'une ligne d'en-tête, ou est vide. Ajoutez vos lignes de
                données horaires.</td>
            </tr>
            <tr>
              <td><Code>period_start must be before period_end</Code></td>
              <td>(Fichiers de factures d'énergie) La date de début d'une facture est identique ou
                postérieure à sa date de fin. Corrigez les dates.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Avertissements jaunes (fichier toujours accepté)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Message</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>Extra columns found and will be ignored</Code></td><td>Votre fichier contient des colonnes que la plateforme n'utilise pas. Sans conséquence — elles sont simplement ignorées.</td></tr>
            <tr><td><Code>... gap(s) detected in the time series</Code></td><td>Certaines heures sont manquantes entre votre première et votre dernière ligne. Ajoutez les heures manquantes pour une meilleure précision.</td></tr>
            <tr><td><Code>timestamp(s) are not in chronological order</Code></td><td>Les lignes ne sont pas triées par ordre chronologique. Triez le fichier par la colonne d'horodatage.</td></tr>
            <tr><td><Code>has ... missing value(s)</Code></td><td>Certaines cellules d'une colonne numérique sont vides. Remplissez-les (utilisez <Code>0</Code> si approprié).</td></tr>
            <tr><td><Code>... potential outliers</Code></td><td>Quelques valeurs sont inhabituellement élevées ou basses. Vérifiez-les en cas de faute de frappe, sinon ignorez.</td></tr>
            <tr><td><Code>contains all zero values</Code></td><td>Toute une colonne est à zéro. Normal pour le solaire nocturne ; sinon vérifiez que vous avez exporté les bonnes données.</td></tr>
          </tbody>
        </Table>
        <Tip>
          La plupart des erreurs rouges proviennent de la virgule décimale ou d'en-têtes mal orthographiés.
          Partir d'un modèle téléchargé évite les deux — voir <em>« Créer des fichiers CSV correctement »</em>.
        </Tip>
      </>
    ),
  },

  {
    id: 'tb-forecast-flat',
    category: 'troubleshooting',
    title: 'Ma prévision semble plate ou incorrecte',
    summary: 'Quand la courbe prédite est une ligne plate, une copie de la veille, ou complètement erronée — que vérifier.',
    body: (
      <>
        <p>Si une prévision semble inutile, l'une de ces causes est presque toujours en jeu.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Ce que vous voyez</th><th>Cause la plus probable</th><th>Que faire</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Ligne plate</strong> — la prédiction est une valeur unique sur tout l'horizon.</td>
              <td>Très peu d'historique en entrée, ou l'entrée est principalement à zéro / une seule valeur répétée.</td>
              <td>Téléversez plus d'historique (au moins un mois, idéalement un an). Vérifiez que le fichier ne contient pas une seule valeur répétée.</td>
            </tr>
            <tr>
              <td><strong>« Copie de la veille »</strong> — la prédiction ressemble aux 24 dernières heures, décalées.</td>
              <td>Le modèle a basculé sur une base naïve car les données sont trop courtes pour ajuster un vrai modèle.</td>
              <td>Ajoutez plus d'historique. Ajoutez un fichier météo pour les prévisions solaires (voir <ArticleLink to="data-weather">Fichiers météo</ArticleLink>).</td>
            </tr>
            <tr>
              <td><strong>Pics / valeurs négatives</strong> dans la prédiction.</td>
              <td>Des valeurs aberrantes dans l'historique (ex. : défaut de compteur) perturbent le modèle.</td>
              <td>Ouvrez le fichier d'historique, trouvez la ligne suspecte, corrigez-la ou supprimez-la, téléversez à nouveau, relancez.</td>
            </tr>
            <tr>
              <td><strong>Bande de confiance très large</strong>.</td>
              <td>Le modèle sait qu'il est incertain — généralement dû à un historique court ou à des données récentes inhabituelles.</td>
              <td>Ajoutez plus de données ; la bande devrait se rétrécir à la prochaine exécution.</td>
            </tr>
            <tr>
              <td><strong>Notice « données d'exemple » sur la page</strong>.</td>
              <td>Le service de prévision était indisponible ou les données étaient insuffisantes — des résultats illustratifs sont affichés.</td>
              <td>Vérifiez la page de téléversement pour les fichiers manquants ; réessayez dans quelques minutes.</td>
            </tr>
            <tr>
              <td><strong>La prévision solaire est approximativement correcte mais l'amplitude est décalée</strong>.</td>
              <td>La capacité PV ou l'orientation des panneaux peut être mal configurée sur l'équipement, ou le fichier de production utilise kWh là où kW est attendu (ou vice versa).</td>
              <td>Vérifiez la capacité installée de l'équipement (kWp). Vérifiez que l'unité du fichier de production correspond à ce qu'attend la plateforme sur la page de téléversement.</td>
            </tr>
          </tbody>
        </Table>

        <Tip>
          Un bon test de cohérence : si vous prévoyez un <em>jour passé</em> d'un bâtiment et que
          la prédiction ne ressemble pas du tout à ce qui s'est passé, le modèle manque de données.
          Ajouter de l'historique est presque toujours plus efficace que de modifier les paramètres.
        </Tip>
      </>
    ),
  },

  {
    id: 'tb-optimisation-infeasible',
    category: 'troubleshooting',
    title: 'L\'optimisation indique « infaisable »',
    summary: 'Quand l\'exécution d\'Optimisation échoue à trouver un programme valide — causes fréquentes et solutions.',
    body: (
      <>
        <p>
          « Infaisable » signifie que l'optimiseur n'a pas pu trouver <em>aucun</em> programme
          satisfaisant toutes les contraintes simultanément. Généralement, une contrainte est trop
          stricte.
        </p>

        <h5 className="mt-3">Points à vérifier, dans l'ordre</h5>
        <ol>
          <li>
            <strong>Capacité de la batterie par rapport à la demande.</strong> Si vous avez forcé
            la batterie à couvrir le pic du soir mais que sa capacité est trop faible, l'optimiseur
            n'a pas de plan valide. Augmentez la capacité de la batterie, ou assouplissez la
            contrainte.
          </li>
          <li>
            <strong>Fenêtre de branchement du véhicule électrique par rapport à l'énergie requise.</strong>{' '}
            Si un EV a besoin de 40 kWh mais n'est branché que 2 heures à 7 kW (= 14 kWh maximum),
            il ne peut pas être rechargé complètement. Élargissez la fenêtre ou réduisez le SOC cible.
          </li>
          <li>
            <strong>Objectif d'autosuffisance par rapport à la production.</strong> Un objectif de
            « 100 % d'autosuffisance » avec peu de PV et aucune importation réseau autorisée est
            impossible. Abaissez l'objectif ou autorisez une certaine importation.
          </li>
          <li>
            <strong>Bornes SOC min/max.</strong> Si le SOC minimum est supérieur au SOC maximum
            (ou si les deux sont fixés au même niveau), il n'y a pas de marge de manœuvre.
            Vérifiez les deux bornes.
          </li>
          <li>
            <strong>Prix de tarif négatifs.</strong> Certaines exportations affichent des prix de
            gros négatifs sur les marchés réels. Si vous avez défini une règle « export ≥ 0 »,
            ces heures deviennent infaisables. Supprimez la règle, ou pré-filtrez le fichier de
            tarif.
          </li>
          <li>
            <strong>Données manquantes dans l'horizon.</strong> Une lacune dans le fichier de
            charge ou de tarif à l'intérieur de l'horizon d'optimisation peut bloquer le solveur.
            Téléversez à nouveau le fichier avec la lacune comblée (utilisez la valeur de l'heure
            précédente ou 0).
          </li>
        </ol>

        <h5 className="mt-3">Comment diagnostiquer rapidement</h5>
        <p>
          Relancez avec l'objectif <strong>« Minimiser le coût »</strong> et <em>aucun</em> objectif
          strict d'autosuffisance / d'autoconsommation. Si cela réussit, la contrainte bloquante
          était votre objectif — assouplissez-le. Si cela échoue encore, la contrainte bloquante
          est la capacité d'un équipement ou une lacune dans les données.
        </p>
        <Tip>
          Diviser l'horizon par deux (24 h → 12 h) réussit parfois quand 24 h échoue. Cela pointe
          vers un bloc horaire spécifique où se situe le problème — inspectez ce bloc dans vos données.
        </Tip>
      </>
    ),
  },

  {
    id: 'tb-kpi-sanity-check',
    category: 'troubleshooting',
    title: 'Mes KPI semblent trop bons — ou trop mauvais',
    summary: 'Quand l\'autosuffisance est à 99 %, la VAN est énorme, ou le retour sur investissement est invraisemblablement court — que vérifier.',
    body: (
      <>
        <p>
          Un KPI qui semble « trop beau pour être vrai » provient presque toujours d'un petit ensemble
          d'erreurs de données. Effectuez ces vérifications avant de partager le résultat.
        </p>

        <h5 className="mt-3">Si l'autosuffisance ou l'autoconsommation est invraisemblablement élevée</h5>
        <ul>
          <li><strong>Fichier de consommation à une échelle trop petite.</strong> Des valeurs en kW saisies dans une colonne kWh (ou vice versa) font paraître la communauté très petite par rapport à son PV. Revérifiez l'unité sur la page de téléversement.</li>
          <li><strong>Seulement une année partielle téléversée.</strong> Si vous n'avez téléversé que les mois d'été, il est évident que le solaire couvre la majeure partie de la demande. Téléversez une année complète pour un chiffre représentatif.</li>
          <li><strong>Bâtiments manquants.</strong> Un seul des trois bâtiments est dans la plateforme, donc la communauté paraît plus petite et davantage couverte par le solaire qu'en réalité.</li>
        </ul>

        <h5 className="mt-3">Si la VAN ou les économies annuelles sont invraisemblablement élevées</h5>
        <ul>
          <li><strong>Tarif dans les mauvaises unités.</strong> €/MWh traité comme €/kWh gonfle les économies de 1000×. Ouvrez le fichier de tarif — un prix de <Code>120</Code> devrait être <Code>0.12</Code> s'il représente €/kWh.</li>
          <li><strong>Durée de vie trop longue.</strong> Un horizon de 30 ans pour une batterie de 15 ans surestime les économies. Faites correspondre la durée de vie à l'équipement.</li>
          <li><strong>Taux d'actualisation à 0 %.</strong> Sans actualisation, les économies futures comptent pleinement — généralement irréaliste. Utilisez 3–6 % sauf indication contraire de votre équipe financière.</li>
          <li><strong>Hypothèse d'inflation trop agressive.</strong> Si vous supposez que les prix de l'électricité augmentent de 8 % par an indéfiniment, les économies explosent. Utilisez un chiffre raisonnable (1–4 %).</li>
        </ul>

        <h5 className="mt-3">Si les résultats semblent invraisemblablement mauvais</h5>
        <ul>
          <li><strong>Fichier de tarif manquant ou tout à zéro.</strong> Pas de prix = pas d'économies à comptabiliser. Téléversez le tarif et relancez.</li>
          <li><strong>Capacité PV laissée à 0 kWp.</strong> Vérifiez l'équipement sur la page des équipements de la communauté.</li>
          <li><strong>Mauvais rendement de la batterie.</strong> Une valeur par défaut de 50 % lui ferait « perdre » la moitié de chaque kWh — vérifiez le rendement aller-retour de l'équipement.</li>
          <li><strong>Fichier de production dans la mauvaise colonne.</strong> Si la consommation et la production ont été échangées, l'optimiseur voit le bâtiment ne rien produire.</li>
        </ul>

        <Tip>
          Un test de cohérence utile : divisez les économies annuelles par la facture annuelle
          d'électricité de la communauté. Si le résultat dépasse 50–70 %, quelque chose est
          probablement surestimé. Les projets d'énergie communautaire réels réduisent typiquement
          les factures de 10–40 %.
        </Tip>
      </>
    ),
  },

  {
    id: 'tb-faq',
    category: 'troubleshooting',
    title: 'Questions fréquemment posées',
    summary: 'Réponses courtes aux questions courantes.',
    body: (
      <>
        <h5>Ai-je besoin d'une année complète de données ?</h5>
        <p>Une année complète (8 760 lignes horaires) donne les meilleurs résultats, mais les outils
          fonctionnent avec moins. Au moins un jour (24 lignes) est requis pour les fichiers
          basés sur le temps.</p>

        <h5 className="mt-3">Mon fichier utilise un point-virgule ( ; ) entre les valeurs — est-ce un problème ?</h5>
        <p>Non. La plateforme détecte automatiquement les virgules, points-virgules et tabulations.
          Le séparateur décimal à l'intérieur des nombres doit quand même être un point.</p>

        <h5 className="mt-3">Puis-je téléverser à nouveau le même type de fichier ?</h5>
        <p>Oui — un nouveau téléversement remplace le fichier précédent pour ce type (pour les
          tarifs Day-Ahead, chaque fichier journalier est conservé séparément par date).</p>

        <h5 className="mt-3">Les résultats ressemblent à des données d'exemple — pourquoi ?</h5>
        <p>Si un service de calcul est temporairement indisponible, ou si les données requises n'ont
          pas encore été téléversées, la plateforme affiche des résultats illustratifs clairement
          identifiés afin que vous puissiez continuer à explorer. Téléversez des données réelles
          et relancez l'outil pour obtenir de vrais résultats.</p>

        <h5 className="mt-3">Pourquoi un bouton est-il grisé ?</h5>
        <p>Certains comptes sont en lecture seule (rôle observateur), et certaines actions
          nécessitent que des données soient téléversées au préalable. Survolez le bouton ou
          consultez les indications de la page pour voir ce qui est requis.</p>
      </>
    ),
  },
]
